import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Package, Clock, Warehouse, Copy, Check } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * LotPublic — página pública, sin login, para compartir un lote a mayoreo.
 *
 * Muestra: título, nota del vendedor, precio total del lote, cantidad total
 * de piezas, y una grilla con foto + nombre + descripción + stock de cada
 * producto. Nunca muestra precios individuales ni costo.
 *
 * El link expira 6 días después de creado. Si el backend devuelve 410 mostramos
 * un estado explícito de expirado; 404 es "no existe".
 */
export default function LotPublic() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [copied, setCopied] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/lots/${token}`);
        if (!cancelled) setState({ loading: false, data, error: null });
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail || "";
        const kind = status === 410 ? "expired" : status === 404 ? "not_found" : "error";
        setState({ loading: false, data: null, error: { kind, detail } });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-100 text-zinc-500">
        <div className="animate-pulse">Cargando lote…</div>
      </div>
    );
  }

  if (state.error) {
    const { kind, detail } = state.error;
    const map = {
      expired: {
        title: "Este link ya expiró",
        desc: "La oferta tenía una validez de 6 días. Contacta al vendedor para recibir un nuevo link con la lista actualizada.",
        icon: <Clock size={44} weight="duotone" className="text-[#E0000F]" />,
      },
      not_found: {
        title: "Lote no encontrado",
        desc: "Verifica que el link esté completo o solicita uno nuevo al vendedor.",
        icon: <Package size={44} weight="duotone" className="text-zinc-400" />,
      },
      error: {
        title: "No pudimos cargar el lote",
        desc: detail || "Intenta nuevamente en unos momentos.",
        icon: <Package size={44} weight="duotone" className="text-zinc-400" />,
      },
    };
    const m = map[kind] || map.error;
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-100 px-6">
        <div className="max-w-md w-full bg-white rounded-lg border border-zinc-200 p-8 text-center shadow-sm">
          <div className="mx-auto w-16 h-16 rounded-full bg-zinc-100 flex items-center justify-center mb-4">
            {m.icon}
          </div>
          <h1 className="font-heading text-2xl font-extrabold text-zinc-900">{m.title}</h1>
          <p className="text-sm text-zinc-500 mt-2">{m.desc}</p>
        </div>
      </div>
    );
  }

  const d = state.data;
  const fmt = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const pricePrefix = d.lot_currency === "USD" ? "$" : "MX$";
  const totalStr = `${pricePrefix}${fmt.format(d.lot_price || 0)}`;

  // Countdown / freshness pill
  const expDate = d.expires_at ? new Date(d.expires_at) : null;
  const daysLeft = expDate ? Math.max(0, Math.ceil((expDate - new Date()) / (24 * 3600 * 1000))) : null;

  return (
    <div className="min-h-screen bg-zinc-100 pb-16" data-testid="lot-public-page">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                Oferta de lote — venta al mayoreo
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-zinc-900 break-words" data-testid="lot-title">
                {d.title}
              </h1>
              {d.seller && (
                <div className="text-xs text-zinc-500 mt-2">
                  Ofrece: <span className="font-semibold text-zinc-700">{d.seller}</span>
                </div>
              )}
              {daysLeft !== null && (
                <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-[11px] font-semibold">
                  <Clock size={12} weight="bold" />
                  {daysLeft <= 0
                    ? "Expira hoy"
                    : daysLeft === 1
                      ? "Expira mañana"
                      : `Válido por ${daysLeft} días más`}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={copyLink}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 text-sm"
              data-testid="lot-copy-link"
            >
              {copied ? <Check size={14} weight="bold" className="text-emerald-500" /> : <Copy size={14} weight="bold" />}
              {copied ? "Copiado" : "Copiar link"}
            </button>
          </div>
        </div>
      </header>

      {/* Big total price panel */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
        <div className="bg-gradient-to-br from-[#002FA7] to-[#0043c2] text-white rounded-lg p-6 sm:p-8 shadow-md" data-testid="lot-price-banner">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-white/70">
                Precio total del lote
              </div>
              <div className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight mt-1 numeric" data-testid="lot-total-price">
                {totalStr}
              </div>
              <div className="text-white/80 text-sm mt-1">
                {d.lot_currency === "USD" ? "Dólares americanos" : "Pesos mexicanos"} — incluye {d.product_count} producto{d.product_count === 1 ? "" : "s"} · {d.total_stock.toLocaleString("es-MX")} piezas totales
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <div className="text-[11px] font-mono uppercase tracking-widest text-white/70">
                Contenido del lote
              </div>
              <div className="flex items-center gap-4">
                <Stat label="Productos" value={d.product_count} />
                <Stat label="Piezas" value={d.total_stock} />
              </div>
            </div>
          </div>
          {d.notes && (
            <div className="mt-5 pt-5 border-t border-white/20 text-sm text-white/90 whitespace-pre-line" data-testid="lot-notes">
              {d.notes}
            </div>
          )}
        </div>
      </div>

      {/* Product grid */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-8">
        <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">
          Contenido detallado — {d.products.length} artículo{d.products.length === 1 ? "" : "s"}
        </div>
        {d.products.length === 0 ? (
          <div className="bg-white border border-dashed border-zinc-300 rounded-lg p-10 text-center text-zinc-400">
            El lote está vacío.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {d.products.map((p) => (
              <LotProductCard key={p.id} product={p} onOpenPhoto={setLightbox} />
            ))}
          </div>
        )}
      </div>

      {/* Simple lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="lot-lightbox"
        >
          <img src={lightbox} alt="preview" className="max-w-full max-h-full object-contain rounded" />
        </div>
      )}

      <footer className="max-w-6xl mx-auto px-4 sm:px-6 mt-10 text-center text-[11px] text-zinc-400">
        Link válido hasta el {expDate ? expDate.toLocaleString("es-MX") : "—"}.
      </footer>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="text-right">
      <div className="text-[10px] font-mono uppercase tracking-widest text-white/70">{label}</div>
      <div className="font-heading text-2xl font-extrabold tracking-tight numeric">
        {typeof value === "number" ? value.toLocaleString("es-MX") : value}
      </div>
    </div>
  );
}

function LotProductCard({ product, onOpenPhoto }) {
  const [photoIdx, setPhotoIdx] = useState(0);
  const photos = product.photos || [];
  const cover = photos[photoIdx];
  return (
    <article className="bg-white border border-zinc-200 rounded-lg overflow-hidden flex flex-col" data-testid={`lot-product-${product.id}`}>
      <div className="relative aspect-square bg-zinc-100">
        {cover ? (
          <img
            src={cover}
            alt={product.name}
            className="w-full h-full object-cover cursor-zoom-in"
            onClick={() => onOpenPhoto(cover)}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-zinc-300 gap-1">
            <Package size={40} weight="duotone" />
            <div className="text-[10px] uppercase tracking-widest">Sin foto</div>
          </div>
        )}
        {/* Stock badge */}
        <div className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm text-zinc-900 px-2 py-1 rounded text-[11px] font-bold inline-flex items-center gap-1 border border-zinc-200">
          <Warehouse size={12} weight="bold" />
          {product.stock} pz
        </div>
        {/* Photo thumbs */}
        {photos.length > 1 && (
          <div className="absolute bottom-2 left-2 right-2 flex gap-1 overflow-x-auto">
            {photos.slice(0, 6).map((p, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setPhotoIdx(i)}
                className={`w-10 h-10 shrink-0 rounded border-2 overflow-hidden ${i === photoIdx ? "border-white" : "border-transparent opacity-70"}`}
              >
                <img src={p} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="p-3 flex-1 flex flex-col">
        <h3 className="font-heading font-bold text-sm text-zinc-900 leading-snug line-clamp-2">{product.name}</h3>
        <div className="text-[11px] text-zinc-500 mt-1">
          {product.brand || "—"}{product.category ? ` · ${product.category}` : ""}
        </div>
        {product.characteristics && (
          <p className="text-xs text-zinc-600 mt-2 line-clamp-4 whitespace-pre-line">
            {product.characteristics}
          </p>
        )}
      </div>
    </article>
  );
}
